import React, { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BranchContribution } from "@/components/BranchContribution";
import { GapSuggestions } from "@/components/GapSuggestions";
import { BranchSupport } from "@/components/BranchSupport";
import { UnmarkedFirms } from "@/components/UnmarkedFirms";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Building2, Landmark, CheckCircle2, Target, Download, FileText, Search, Radar as RadarIcon } from "lucide-react";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const AMBER = "#D97706";

function HealthBadge({ score }) {
  const c = score >= 70 ? GREEN : score >= 45 ? AMBER : "#DC2626";
  const label = score >= 70 ? "İyi" : score >= 45 ? "Orta" : "Riskli";
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: `${c}1a`, color: c }}>
      {score} · {label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}1a`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold" style={{ color }}>{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
}

export default function BranchScorecard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEditTarget = user?.role === "super_admin" || user?.role === "branch_manager";
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("scorecard");

  const saveTarget = async (branch, val) => {
    const pct = parseInt(val, 10);
    if (!pct || pct < 1 || pct > 100) return;
    try {
      await api.patch("/dashboard/branch-target", { branch, target_pct: pct });
      const { data } = await api.get("/dashboard/branch-scorecard");
      setData(data);
      toast.success(`${branch} hedefi %${pct} olarak ayarlandı`);
    } catch {
      toast.error("Hedef kaydedilemedi");
    }
  };

  useEffect(() => {
    api.get("/dashboard/branch-scorecard")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Şube verileri yüklenemedi"));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    return (data.rows || []).filter((r) => !term || r.branch.toLocaleLowerCase("tr").includes(term));
  }, [data, q]);
  const bsT = useTableSort(rows);

  const download = async (format) => {
    setBusy(true);
    try {
      const order = bsT.sorted.map((r) => r.branch).join(",");
      const res = await api.get("/dashboard/branch-scorecard/export", { params: { format, order, only: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sube_karnesi.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="branch-scorecard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${NAVY}1a`, color: NAVY }}>
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Şube Karnesi</h1>
            <p className="text-sm text-muted-foreground">Şube bazında seçim durumu — FAAL, hedef, KESİN, sağlık</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => download("xlsx")} disabled={busy} data-testid="branch-export-xlsx">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={busy} data-testid="branch-export-pdf">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Landmark} label="Toplam FAAL" value={data?.total_faal ?? 0} color={NAVY} testid="branch-total-faal" />
        <StatCard icon={CheckCircle2} label="Kesin Oy" value={data?.total_secured ?? 0} color={GREEN} testid="branch-total-secured" />
        <StatCard icon={Target} label="Toplam Hedef" value={data?.total_target ?? 0} color={AMBER} testid="branch-total-target" />
        <Card className="p-4 flex items-center gap-3 cursor-pointer hover:border-cyan-500 transition-colors"
          onClick={() => navigate("/radar")} data-testid="branch-total-radar">
          <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "#0891b21a", color: "#0891b2" }}>
            <RadarIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-2xl font-bold" style={{ color: "#0891b2" }}>+{data?.total_radar_potential ?? 0}</div>
            <div className="text-xs text-muted-foreground">
              Radar'dan kazanılabilir oy{data?.total_radar_people ? ` · ${data.total_radar_people} kişi` : ""}
            </div>
          </div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="branch-tabs">
          <TabsTrigger value="scorecard" data-testid="branch-tab-scorecard">Şube Karnesi</TabsTrigger>
          <TabsTrigger value="contribution" data-testid="branch-tab-contribution">Şube Katkı Analizi</TabsTrigger>
          <TabsTrigger value="gaps" data-testid="branch-tab-gaps">Açık Kapatma Önerisi</TabsTrigger>
          {user?.role === "super_admin" && (
            <TabsTrigger value="support" data-testid="branch-tab-support">Şube Destek Platformu</TabsTrigger>
          )}
          <TabsTrigger value="unmarked" data-testid="branch-tab-unmarked">İşaretsiz Takibi</TabsTrigger>
        </TabsList>

        <TabsContent value="unmarked" className="mt-4">
          <UnmarkedFirms />
        </TabsContent>

        <TabsContent value="gaps" className="mt-4">
          <GapSuggestions canAssign={user?.role === "super_admin" || user?.role === "committee_manager"} />
        </TabsContent>

        {user?.role === "super_admin" && (
          <TabsContent value="support" className="mt-4">
            <BranchSupport />
          </TabsContent>
        )}

        <TabsContent value="contribution" className="mt-4">
          <BranchContribution />
        </TabsContent>

        <TabsContent value="scorecard" className="mt-4 space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Şube ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="branch-search" />
      </div>


      {!data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="branch-table">
              <thead>
                <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <bsT.Th sortKey="b-sube" get={(r) => r.branch} text className="px-4 py-3 font-medium">Şube</bsT.Th>
                  <bsT.Th sortKey="b-toplam" get={(r) => r.total} className="px-4 py-3 font-medium text-right">Toplam</bsT.Th>
                  <bsT.Th sortKey="b-faal" get={(r) => r.faal} className="px-4 py-3 font-medium text-right">FAAL</bsT.Th>
                  <bsT.Th sortKey="b-hedef" get={(r) => r.target} className="px-4 py-3 font-medium text-right">Hedef</bsT.Th>
                  <bsT.Th sortKey="b-kesin" get={(r) => r.secured} className="px-4 py-3 font-medium text-right">KESİN</bsT.Th>
                  <bsT.Th sortKey="b-belki" get={(r) => r.belki} className="px-4 py-3 font-medium text-right">Belki</bsT.Th>
                  <bsT.Th sortKey="b-gelmeyecek" get={(r) => r.gelmeyecek} lowBest className="px-4 py-3 font-medium text-right">Gelmeyecek</bsT.Th>
                  <bsT.Th sortKey="b-ulasilamadi" get={(r) => r.ulasilamadi} lowBest className="px-4 py-3 font-medium text-right">Ulaşılamadı</bsT.Th>
                  <bsT.Th sortKey="b-karsi" get={(r) => r.karsi} lowBest className="px-4 py-3 font-medium text-right">Karşı</bsT.Th>
                  <bsT.Th sortKey="b-kalan" get={(r) => r.remaining} lowBest className="px-4 py-3 font-medium text-right">Kalan</bsT.Th>
                  <bsT.Th sortKey="b-radar" get={(r) => r.radar_potential ?? 0} className="px-4 py-3 font-medium text-right">Radar</bsT.Th>
                  <bsT.Th sortKey="b-saglik" get={(r) => r.health} className="px-4 py-3 font-medium text-center">Sağlık</bsT.Th>
                  <bsT.Th sortKey="b-durum" get={(r) => (r.winning ? 1 : 0)} className="px-4 py-3 font-medium text-right">Durum</bsT.Th>
                </tr>
              </thead>
              <tbody>
                {bsT.sorted.map((r) => (
                  <tr key={r.branch} className="border-t border-border/60 hover:bg-muted/40" data-testid={`branch-row-${r.branch}`}>
                    <td className="px-4 py-3 font-medium">{r.branch}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{r.total}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{r.faal}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1">
                        <span>{r.target}</span>
                        {canEditTarget && (
                          <>
                            <input type="number" min="1" max="100" defaultValue={r.target_pct}
                              onBlur={(e) => saveTarget(r.branch, e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                              className="w-14 h-7 text-xs rounded border border-border bg-background px-1 text-right ml-1"
                              data-testid={`branch-target-input-${r.branch}`} title="Hedef % (Enter ile kaydet)" />
                            <span className="text-xs text-muted-foreground">%</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: GREEN }}>{r.secured}</td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: "#F59E0B" }}>{r.belki ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{r.gelmeyecek ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: "#0EA5E9" }}>{r.ulasilamadi ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: "#DC2626" }}>{r.karsi ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold" style={{ color: r.remaining === 0 ? GREEN : AMBER }}>
                      {r.remaining === 0 ? "0" : `-${r.remaining}`}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <button type="button" onClick={() => navigate(`/radar?branch=${encodeURIComponent(r.branch)}`)}
                        className="font-semibold text-cyan-700 dark:text-cyan-300 hover:underline disabled:opacity-40 disabled:no-underline"
                        disabled={!r.radar_potential} data-testid={`branch-radar-${r.branch}`}
                        title={`${r.radar_people || 0} kişi üzerinden kazanılabilir oy`}>
                        +{r.radar_potential ?? 0}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center"><HealthBadge score={r.health} /></td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-semibold" style={{ color: r.winning ? GREEN : AMBER }}>
                        {r.winning ? "Hedefte" : `-${r.remaining}`}
                      </span>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Şube bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <p className="text-xs text-muted-foreground">
        Hedef = şubenin 2018 katılım oranının %30 fazlası kadar firma oy kullanır varsayımıyla, o sayının yarısı + 1 (2018 verisi yoksa manuel/varsayılan oran × 1,30). Kesin Oy = KESİN eğilim + yetki belgesi. Sağlık; kesin oy, hedefe yakınlık ve işaretlenme oranından hesaplanır.
        Radar = o şubedeki eksik/tezat FAAL firmalar üzerinden kazanılabilecek oy; tıklayınca Radar'da o şube faydalanıcı olarak açılır.
      </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
